import type { Granularity } from './kpi-aggregation.js';
import { periodKey } from './kpi-aggregation.js';
import type { PurchaseDailyRecord } from '../types/purchase-analysis.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 購入分析チャート用のデータポイント。粒度集約済み。
 * @graph-connects none
 */
export interface PurchaseChartPoint {
  label: string;
  purchaseCount: number;
  purchaseRevenue: number;
  averageUnitPrice: number;
  deliveryItemCount: number;
  purchaseRate: number;
  purchaseRatePct: number;
}

/** @graph-connects none */
function deriveMetrics(count: number, revenue: number, items: number) {
  const avgPrice = count > 0 ? Math.round(revenue / count) : 0;
  const rate = items > 0 ? Math.round((count / items) * 10000) / 10000 : 0;
  return {
    purchaseCount: count,
    purchaseRevenue: revenue,
    averageUnitPrice: avgPrice,
    deliveryItemCount: items,
    purchaseRate: rate,
    purchaseRatePct: Math.round(rate * 10000) / 100,
  };
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 日次の購入分析レコードを指定粒度で集約する。フロー指標（着数・売上）は合計、派生指標（単価・購入率）は再計算。
 * @graph-connects none
 */
export function aggregatePurchaseRecords(
  rows: PurchaseDailyRecord[],
  granularity: Granularity,
): PurchaseChartPoint[] {
  if (granularity === 'daily') {
    return rows.map((r) => ({
      label: r.date,
      ...deriveMetrics(r.purchaseCount, r.purchaseRevenue, r.deliveryItemCount),
    }));
  }

  const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1));
  const buckets = new Map<string, PurchaseDailyRecord[]>();
  for (const row of sorted) {
    const key = periodKey(row.date, granularity);
    const arr = buckets.get(key);
    if (arr) arr.push(row);
    else buckets.set(key, [row]);
  }

  const result: PurchaseChartPoint[] = [];
  for (const [key, group] of buckets) {
    let totalCount = 0;
    let totalRevenue = 0;
    let totalItems = 0;
    for (const r of group) {
      totalCount += r.purchaseCount;
      totalRevenue += r.purchaseRevenue;
      totalItems += r.deliveryItemCount;
    }
    result.push({ label: key, ...deriveMetrics(totalCount, totalRevenue, totalItems) });
  }
  return result;
}
